import dbStorageService from '../dbStorageService';
import circleService from '../circleService';

/**
 * Handles storage operations for notifications
 */
export const notificationStorage = {
  /**
   * Save notifications to the database
   * @param {Array} notifications - Notifications to save
   * @param {Function} onSuccess - Callback when successful
   * @returns {Promise<boolean>} Whether the save was successful
   */
  async saveNotifications(notifications) {
    try {
      if (!notifications || notifications.length === 0) return true;

      const processedNotifications = [];
      for (const notification of notifications) {
        // Assuming notification types 120 (Medication) and 130 (HealthCheck) are the ones with eventSourceId
        if ((notification.type === '120' || notification.type === '130') && notification.jsonData && notification.jsonData.userConfigUuid) {
          const eventSourceId = notification.jsonData.userConfigUuid;
          const userId = notification.userId;
          const existing = await this.getNotificationByEventSourceId(userId, eventSourceId);

          if (existing) {
            // Update existing notification
            processedNotifications.push({
              ...existing, // Keep original id and uuid
              ...notification, // Apply new data
              id: existing.id, // Ensure original id is kept
              uuid: existing.uuid, // Ensure original uuid is kept
              updatedAt: new Date().toISOString(),
              lastModified: new Date().toISOString(),
            });
          } else {
            // New notification
            processedNotifications.push({
              ...notification,
              createdAt: notification.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              lastModified: new Date().toISOString(),
            });
          }
        } else {
          // For other notification types or if no eventSourceId, treat as new/direct upsert
          processedNotifications.push({
            ...notification,
            // Ensure timestamps if they are new
            createdAt: notification.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastModified: new Date().toISOString(),
          });
        }
      }
      
      await dbStorageService.upsertRecords('Notification', processedNotifications, new Date());
      return true;
    } catch (error) {
      console.error('Error saving notifications:', error);
      throw error;
    }
  },
  
  /**
   * Get all notifications for a user and users in their circle of trust
   * @param {number} userId - The user ID
   * @returns {Promise<Array>} List of notifications
   */
  async getNotifications(userId) {
    try {
      // Fetch notifications from IndexedDB for current user first
      await dbStorageService.initialize();
      
      // Check if Notification table exists
      if (!dbStorageService.tableExists('Notification')) {
        console.warn('[notificationStorage] Notification table does not exist yet, returning empty results');
        return [];
      }
      
      let allNotifications = await dbStorageService.getRecordsByIndex('Notification', 'userId', userId) || [];
      
      try {
        // Try to get circle members that this user has access to
        const circleMembers = await circleService.getMembersInSameCircles(userId) || [];
        
        // Add user IDs from circle members
        const memberUserIds = circleMembers.map(member => member.userId).filter(Boolean);
        
        // Get notifications for each member user ID
        for (const id of memberUserIds) {
          if (id === userId) continue; // Skip current user as we already got those
          
          try {
            const userNotifications = await dbStorageService.getRecordsByIndex('Notification', 'userId', id) || [];
            
            // Add user info to each notification for display purposes
            const member = circleMembers.find(m => m.userId === id);
            if (member) {
              const memberName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
              userNotifications.forEach(notification => {
                notification.forUserId = id;
                notification.forUserName = memberName || `User ${id}`;
              });
            }
            
            allNotifications = [...allNotifications, ...userNotifications];
          } catch (error) {
            console.warn(`Failed to get notifications for user ${id}:`, error);
            // Continue with other users even if one fails
          }
        }
      } catch (error) {
        console.warn('Failed to get circle members:', error);
        // Continue with just the current user's notifications
      }
      
      // Filter out deleted notifications
      return allNotifications.filter(n => !n.isDeleted);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  },
  
  /**
   * Mark a notification as read
   * @param {string} uuid - The notification UUID
   * @param {boolean} isRead - Whether to mark as read or unread
   * @returns {Promise<Object|null>} The updated notification or null if not found
   */
  async markAsRead(uuid, isRead = true) {
    try {
      await dbStorageService.initialize();
      
      // Check if Notification table exists
      if (!dbStorageService.tableExists('Notification')) {
        console.warn('[notificationStorage] Notification table does not exist yet, cannot mark as read');
        return null;
      }
      
      // Get the notification
      const notifications = await dbStorageService.getRecordsByField('Notification', 'uuid', uuid);
      
      if (!notifications || notifications.length === 0) {
        console.warn(`Notification not found: ${uuid}`);
        return null;
      }
      
      const notification = notifications[0];
      
      // Update the notification
      const updatedNotification = {
        ...notification,
        isRead,
        lastModified: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Save the updated notification
      await dbStorageService.upsertRecords('Notification', [updatedNotification], new Date());
      
      return updatedNotification;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return null;
    }
  },
  
  /**
   * Mark a notification for deletion
   * @param {string} uuid - The notification UUID
   * @returns {Promise<Object|null>} The deleted notification or null if not found
   */
  async deleteNotification(uuid) {
    try {
      await dbStorageService.initialize();
      
      // Check if Notification table exists
      if (!dbStorageService.tableExists('Notification')) {
        console.warn('[notificationStorage] Notification table does not exist yet, cannot delete notification');
        return null;
      }
      
      // Get the notification first to ensure it exists
      const notifications = await dbStorageService.getRecordsByField('Notification', 'uuid', uuid);
      
      if (notifications.length === 0) {
        console.warn(`[NotificationStorage] Notification ${uuid} not found for deletion`);
        return null;
      }
      
      const notification = notifications[0];
      
      // Mark as deleted
      const deletedNotification = {
        ...notification,
        isDeleted: true,
        lastModified: new Date().toISOString()
      };
      
      // Update in database
      await dbStorageService.upsertRecords('Notification', [deletedNotification]);
      
      return deletedNotification;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return null;
    }
  },
  
  /**
   * Clean up duplicate notifications
   * @param {Array} notifications - List of notifications
   * @returns {Promise<number>} Number of duplicates removed
   */
  async cleanupDuplicatesByUuid(uuids) {
    if (!uuids || uuids.length === 0) return 0;
    
    try {
      await dbStorageService.markForDeletion('Notification', uuids);
      return uuids.length;
    } catch (error) {
      console.error('Error cleaning up notifications by UUID:', error);
      return 0;
    }
  },

  /**
   * Get a notification by its event source ID (userConfigUuid in jsonData)
   * @param {number} userId - The user ID
   * @param {string} eventSourceId - The event source ID (userConfigUuid)
   * @returns {Promise<Object|null>} The notification or null if not found
   */
  async getNotificationByEventSourceId(userId, eventSourceId) {
    try {
      await dbStorageService.initialize();
      if (!dbStorageService.tableExists('Notification')) {
        console.warn('[notificationStorage] Notification table does not exist yet.');
        return null;
      }

      // This is a simplified assumption. dbStorageService might need a more specific
      // method to query based on a value within a JSON field, or we might need to fetch
      // all by userId and then filter.
      // For now, assuming getRecordsByField can somehow handle this, or it's a placeholder for more complex logic.
      // A more realistic approach for IndexedDB might be to fetch all by userId and filter client-side.
      const userNotifications = await dbStorageService.getRecordsByIndex('Notification', 'userId', userId);
      if (!userNotifications || userNotifications.length === 0) {
        return null;
      }

      const foundNotification = userNotifications.find(n =>
        !n.isDeleted &&
        n.jsonData &&
        n.jsonData.userConfigUuid === eventSourceId &&
        (n.type === '120' || n.type === '130') // Ensure it's a relevant type
      );

      return foundNotification || null;

    } catch (error) {
      console.error(`Error fetching notification by eventSourceId (${eventSourceId}) for user ${userId}:`, error);
      return null;
    }
  }
}; 